import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { useReviewDraft } from '../contexts/ReviewDraftContext';
import { API_URL } from '../utils/config';
import './DraftReviewPage.css';

export interface DraftReviewLocationState {
  draftReport: Record<string, unknown>;
  draftCaseNumber: string;
  draftImageCaptions: Record<string, string>;
  draftImageFilenames: string[];
  draftAerialFilenames: string[];
  draftValueTypes: Record<string, 'string' | 'array' | 'object'>;
  researchContext?: string;
  transcript?: string;
  engineerNotes?: string;
  windReport?: { summary?: string; extracted_data?: unknown; source_file?: string } | null;
}

function formatDraftValue(v: unknown): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map((x) => String(x)).join('\n');
  if (v !== null && typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v ?? '');
}

function parseDraftValue(s: string, type: 'string' | 'array' | 'object'): unknown {
  if (type === 'string') return s;
  if (type === 'array') return s.split('\n').map((l) => l.trim()).filter(Boolean);
  if (type === 'object') {
    try {
      return JSON.parse(s);
    } catch {
      return s;
    }
  }
  return s;
}

const DraftReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  const reviewDraft = useReviewDraft();
  const state = location.state as (DraftReviewLocationState & { draftCaseNumber?: string }) | null;
  const caseNumberFromNav = state?.draftCaseNumber ?? new URLSearchParams(location.search).get('case') ?? '';

  const [fetchedDraft, setFetchedDraft] = useState<DraftReviewLocationState | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const effectiveDraft = state?.draftReport ? state : fetchedDraft;

  const draftCaseNumber = effectiveDraft?.draftCaseNumber ?? caseNumberFromNav;
  const draftImageCaptions = effectiveDraft?.draftImageCaptions ?? {};
  const draftImageFilenames = effectiveDraft?.draftImageFilenames ?? [];
  const draftAerialFilenames = effectiveDraft?.draftAerialFilenames ?? [];
  const draftValueTypes = effectiveDraft?.draftValueTypes ?? {};

  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [generateDownloadLoading, setGenerateDownloadLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [results, setResults] = useState<{ case_number: string; report_download_url: string } | null>(null);
  const [downloadReportLoading, setDownloadReportLoading] = useState<string | null>(null);
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const lastInitialDraftRef = useRef<Record<string, unknown> | null>(null);
  const fetchStartedRef = useRef(false);

  // Load draft by claim number (API field: case_number) when we have id but no full state
  useEffect(() => {
    if (!caseNumberFromNav || state?.draftReport || fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    setLoadingDraft(true);
    axios
      .get(`${API_URL.replace(/\/$/, '')}/api/analysis/draft`, {
        params: { case_number: caseNumberFromNav },
        withCredentials: true,
        headers: getAuthHeaders(),
      })
      .then((res) => {
        if (!res.data?.success || !res.data?.draft_report) {
          navigate('/cases', { replace: true });
          return;
        }
        const draft = res.data.draft_report as Record<string, unknown>;
        const types: Record<string, 'string' | 'array' | 'object'> = {};
        Object.entries(draft).forEach(([k, v]) => {
          if (typeof v === 'string') types[k] = 'string';
          else if (Array.isArray(v)) types[k] = 'array';
          else if (v !== null && typeof v === 'object') types[k] = 'object';
          else types[k] = 'string';
        });
        const built: DraftReviewLocationState = {
          draftReport: draft,
          draftCaseNumber: res.data.case_number ?? caseNumberFromNav,
          draftImageCaptions: res.data.image_captions ?? {},
          draftImageFilenames: res.data.image_filenames ?? [],
          draftAerialFilenames: res.data.aerial_filenames ?? [],
          draftValueTypes: types,
          researchContext: res.data.research_context ?? '',
          transcript: res.data.transcript ?? '',
          engineerNotes: res.data.engineer_notes ?? '',
          windReport: res.data.wind_report ?? null,
        };
        setFetchedDraft(built);
      })
      .catch(() => {
        navigate('/cases', { replace: true });
      })
      .finally(() => {
        setLoadingDraft(false);
      });
  }, [caseNumberFromNav, state?.draftReport, navigate, getAuthHeaders]);

  // Sync initial draft and original context (from state or fetched) into ReviewDraftContext
  useEffect(() => {
    if (!effectiveDraft?.draftReport || !reviewDraft) return;
    if (lastInitialDraftRef.current === effectiveDraft.draftReport) return;
    lastInitialDraftRef.current = effectiveDraft.draftReport;
    const originalContext = {
      research_context: effectiveDraft.researchContext ?? '',
      transcript: effectiveDraft.transcript ?? '',
      engineer_notes: effectiveDraft.engineerNotes ?? '',
      wind_report: effectiveDraft.windReport ?? undefined,
    };
    reviewDraft.setVersionsFromInitial(effectiveDraft.draftReport, originalContext);
  }, [effectiveDraft?.draftReport, effectiveDraft?.researchContext, effectiveDraft?.transcript, effectiveDraft?.engineerNotes, effectiveDraft?.windReport, reviewDraft]);

  // Current draft is the selected version from context; fallback to effective draft until context is populated
  const draftReport: Record<string, unknown> | null =
    reviewDraft && reviewDraft.versions.length > 0
      ? (reviewDraft.versions[reviewDraft.activeVersionIndex] ?? null)
      : (effectiveDraft?.draftReport ?? null);

  // Redirect when we have no way to get a draft
  useEffect(() => {
    if (caseNumberFromNav || state?.draftReport) return;
    navigate('/analysis', { replace: true });
  }, [caseNumberFromNav, state?.draftReport, navigate]);

  useEffect(() => {
    Object.values(textareaRefs.current).forEach((el) => {
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  }, [draftReport]);

  const handleDraftSectionChange = (key: string, rawValue: string) => {
    if (!reviewDraft || !draftReport) return;
    const type = draftValueTypes[key] ?? 'string';
    const parsed = parseDraftValue(rawValue, type);
    const updated = { ...draftReport, [key]: parsed };
    reviewDraft.updateVersionAt(reviewDraft.activeVersionIndex, updated);
  };

  const handleTextareaResize = (key: string) => {
    const el = textareaRefs.current[key];
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const handleGenerateDownload = async () => {
    if (!draftReport || !draftCaseNumber) return;
    setGenerateDownloadLoading(true);
    setError('');
    try {
      const response = await axios.post<{
        success: boolean;
        download_url?: string;
        suggested_filename?: string;
      }>(
        `${API_URL.replace(/\/$/, '')}/api/reports/generate-download`,
        {
          case_number: draftCaseNumber,
          draft_report: draftReport,
          image_captions: draftImageCaptions,
          image_filenames: draftImageFilenames,
          aerial_filenames: draftAerialFilenames,
        },
        { withCredentials: true, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      if (response.status === 200 && response.data?.success && response.data?.download_url) {
        const a = document.createElement('a');
        a.href = response.data.download_url;
        a.download =
          response.data.suggested_filename ?? `draft_${draftCaseNumber}.docx`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        setError('Generate & download failed');
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object' && !(err.response.data instanceof Blob)) {
        const msg = (err.response.data as { error?: string }).error;
        setError(msg || 'Generate & download failed');
      } else {
        setError(err instanceof Error ? err.message : 'Generate & download failed');
      }
    } finally {
      setGenerateDownloadLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!draftReport || !draftCaseNumber) return;
    setFinalizeLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_URL.replace(/\/$/, '')}/api/reports/finalize`,
        {
          case_number: draftCaseNumber,
          draft_report: draftReport,
          image_captions: draftImageCaptions,
          image_filenames: draftImageFilenames,
          aerial_filenames: draftAerialFilenames,
        },
        { withCredentials: true, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      if (response.data?.success) {
        setResults({
          case_number: response.data.case_number,
          report_download_url: `${API_URL}/api/reports/${response.data.case_number}/download`,
        });
        alert(`Report submitted successfully! Claim number: ${response.data.case_number}`);
      } else {
        setError(response.data?.error || 'Submit failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to submit report');
    } finally {
      setFinalizeLoading(false);
    }
  };

  const handleDownloadReport = async (url: string, label: string) => {
    setDownloadReportLoading(label);
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { ...getAuthHeaders() },
      });
      if (response.status === 401) {
        setError('Session expired. Please log in again.');
        return;
      }
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i);
      const filename = match?.[1] || `${results?.case_number || 'report'}_report.docx`;
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(objectUrl);
      a.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadReportLoading(null);
    }
  };

  if (loadingDraft) {
    return (
      <div className="draft-review-page">
        <Navigation />
        <div className="draft-review-container" style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading draft...</p>
        </div>
      </div>
    );
  }

  if (!effectiveDraft?.draftReport) {
    return null;
  }

  return (
    <div className="draft-review-page">
      <Navigation />
      <div className="draft-review-container">
        <div className="hero-section">
          <div className="hero-pattern" />
          <div className="hero-content">
            <div className="hero-left">
              <h1 className="hero-title">Review report content</h1>
            </div>
            <div className="hero-right">
              <p className="hero-description">
                Edit each section below as needed, then click &quot;Generate report&quot; to create the final document.
              </p>
              <div className="hero-actions">
                <button type="button" onClick={() => navigate('/analysis')} className="hero-cta-btn secondary">
                  <i className="fas fa-arrow-left" />
                  Back to analysis
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="draft-review-content">
          {error && (
            <div className="draft-review-error">
              <p>{error}</p>
            </div>
          )}

          {results && (
            <div className="draft-review-success">
              <p>Report submitted successfully. Use the buttons below to download the submitted report.</p>
            </div>
          )}

          {draftReport && (
            <>
              {reviewDraft && reviewDraft.versions.length > 0 && (
                <div className="draft-version-tabs">
                  {reviewDraft.versions.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`draft-version-tab${i === reviewDraft.activeVersionIndex ? ' active' : ''}`}
                      onClick={() => reviewDraft.setActiveVersionIndex(i)}
                    >
                      Version {i + 1}
                    </button>
                  ))}
                </div>
              )}
              <div className="draft-case-info">
                <strong>Claim number:</strong> {draftCaseNumber}
              </div>
              <div className="draft-sections">
                {Object.keys(draftReport).map((key) => (
                  <div key={key} className="draft-section form-section">
                    <label className="draft-section-label">{key}</label>
                    <textarea
                      ref={(el) => { textareaRefs.current[key] = el; }}
                      className="draft-section-textarea form-input"
                      value={formatDraftValue(draftReport[key])}
                      onChange={(e) => {
                        handleDraftSectionChange(key, e.target.value);
                        handleTextareaResize(key);
                      }}
                      onInput={() => handleTextareaResize(key)}
                      rows={3}
                      spellCheck
                      style={{ minHeight: '4rem', resize: 'vertical' }}
                    />
                  </div>
                ))}
              </div>
              <div className="draft-actions">
                <button
                  type="button"
                  className="download-button primary"
                  onClick={handleGenerateDownload}
                  disabled={generateDownloadLoading}
                >
                  <i className="fas fa-file-download" />
                  {generateDownloadLoading ? 'Generating...' : 'Generate & Download report'}
                </button>
                <button
                  type="button"
                  className="download-button primary"
                  onClick={handleSubmit}
                  disabled={finalizeLoading}
                >
                  <i className="fas fa-paper-plane" />
                  {finalizeLoading ? 'Submitting...' : 'Submit'}
                </button>
                {results && (
                  <button
                    type="button"
                    onClick={() => handleDownloadReport(results.report_download_url, 'download')}
                    disabled={!!downloadReportLoading}
                    className="download-button secondary"
                  >
                    <i className="fas fa-download" />
                    {downloadReportLoading ? 'Downloading...' : 'Download submitted report'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DraftReviewPage;
